import { useState, useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Recruit() {
  const [selectedPosition, setSelectedPosition] = useState<number | null>(null);

  useEffect(() => {
    // Add Schema.org JSON-LD for SEO
    const siteUrl = import.meta.env.VITE_SITE_URL || '';
    
    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "ホーム",
          "item": siteUrl
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "求人情報",
          "item": `${siteUrl}/recruit`
        }
      ]
    };

    const jobPostingSchema = {
      "@context": "https://schema.org",
      "@type": "JobPosting",
      "title": "行政書士補助スタッフ",
      "description": "行政書士業務全般の補助業務を担当していただきます。未経験の方も歓迎いたします。",
      "datePosted": "2026-01-15",
      "validThrough": "2026-12-31",
      "employmentType": "FULL_TIME",
      "hiringOrganization": {
        "@type": "Organization",
        "name": "おかやま行政書士事務所",
        "sameAs": siteUrl,
        "logo": `${siteUrl}/logo.png`
      },
      "jobLocation": {
        "@type": "Place",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "○○町1-2-3",
          "addressLocality": "岡山市北区",
          "addressRegion": "岡山県",
          "postalCode": "700-0000",
          "addressCountry": "JP"
        }
      },
      "baseSalary": {
        "@type": "MonetaryAmount",
        "currency": "JPY",
        "value": {
          "@type": "QuantitativeValue",
          "minValue": 200000,
          "maxValue": 300000,
          "unitText": "MONTH"
        }
      },
      "workHours": "9:00-18:00"
    };

    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.text = JSON.stringify(breadcrumbSchema);
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.type = 'application/ld+json';
    script2.text = JSON.stringify(jobPostingSchema);
    document.head.appendChild(script2);

    return () => {
      document.head.removeChild(script1);
      document.head.removeChild(script2);
    };
  }, []);

  const positions = [
    {
      title: '行政書士補助スタッフ',
      type: '正社員',
      salary: '月給 20万円〜30万円（経験・能力による）',
      image: 'https://public.readdy.ai/ai/img_res/6087397ace6b630bfc0f288e36ff0407.jpg',
      description: '行政書士業務全般の補助業務を担当していただきます。未経験の方も歓迎いたします。',
      responsibilities: [
        '許認可申請書類の作成補助',
        '官公署への書類提出代行',
        'お客様対応（電話・メール・来客）',
        '書類整理・ファイリング',
        'データ入力・管理',
        '行政書士の業務サポート全般'
      ],
      requirements: [
        '高卒以上',
        'Word・Excelの基本操作ができる方',
        '普通自動車運転免許（AT限定可）',
        '明るく丁寧な対応ができる方',
        'コミュニケーション能力のある方'
      ],
      preferred: [
        '行政書士事務所での勤務経験',
        '法律関係の知識・資格',
        '行政書士資格取得を目指している方',
        '岡山市在住の方'
      ],
      benefits: [
        '社会保険完備（健康保険・厚生年金・雇用保険・労災保険）',
        '交通費全額支給',
        '昇給年1回',
        '賞与年2回（業績による）',
        '有給休暇',
        '資格取得支援制度',
        '研修制度充実',
        '駐車場完備'
      ],
      workingHours: '9:00〜18:00（休憩1時間）',
      holidays: '週休2日制（日曜・祝日）、年末年始、夏季休暇、有給休暇',
      location: '岡山市北区○○町1-2-3（JR岡山駅より徒歩10分）'
    },
    {
      title: '事務スタッフ（パート・アルバイト）',
      type: 'パート・アルバイト',
      salary: '時給 1,000円〜1,300円（経験・能力による）',
      image: 'https://readdy.ai/api/search-image?query=Japanese%20female%20part-time%20office%20worker%20in%20casual%20business%20attire%20organizing%20documents%20at%20modern%20office%2C%20friendly%20smile%2C%20bright%20natural%20lighting%2C%20welcoming%20professional%20environment%2C%20high%20quality%20professional%20photography&width=800&height=500&seq=recruit2&orientation=landscape',
      description: '一般事務業務を中心に、柔軟な勤務時間で働いていただけます。主婦の方、学生の方も歓迎です。',
      responsibilities: [
        '電話応対・来客対応',
        'データ入力・書類作成',
        'ファイリング・書類整理',
        '郵便物の発送・受取',
        '事務所内の環境整備',
        'その他庶務業務'
      ],
      requirements: [
        '高卒以上',
        'Word・Excelの基本操作ができる方',
        '明るく丁寧な対応ができる方',
        '週3日以上勤務できる方'
      ],
      preferred: [
        '事務経験のある方',
        '長期勤務できる方',
        '岡山市在住の方'
      ],
      benefits: [
        '交通費支給（規定による）',
        '社会保険完備（勤務時間による）',
        '有給休暇',
        '勤務時間・日数相談可',
        '扶養内勤務可',
        '駐車場完備'
      ],
      workingHours: '9:00〜18:00の間で4時間以上（応相談）',
      holidays: '週休2日制、シフト制（日曜・祝日は休み）',
      location: '岡山市北区○○町1-2-3（JR岡山駅より徒歩10分）'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative h-[500px] sm:h-[600px] lg:h-[500px] bg-gradient-to-r from-blue-900 to-blue-700 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0id2hpdGUiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')]"></div>
        </div>
        <div className="relative h-full flex items-center">
          <div className="w-full max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-4 sm:mb-6">求人情報</h1>
              <p className="text-base sm:text-lg lg:text-xl text-white/90 leading-relaxed">
                おかやま行政書士事務所で一緒に働きませんか？岡山市で地域に貢献できるやりがいのある仕事です。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Message Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">一緒に働く仲間を募集しています</h2>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              おかやま行政書士事務所は、岡山市を中心に地域の皆様の事業と生活をサポートする行政書士事務所です。<br className="hidden sm:block" />
              お客様に寄り添い、誠実・迅速・確実なサービスを提供することを大切にしています。
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12">
            <div className="text-center p-4 sm:p-6 bg-blue-50 rounded-xl">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-team-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">働きやすい環境</h3>
              <p className="text-sm sm:text-base text-gray-700">少人数のアットホームな職場で、一人ひとりを大切にします。</p>
            </div>

            <div className="text-center p-4 sm:p-6 bg-blue-50 rounded-xl">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-book-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">成長できる仕事</h3>
              <p className="text-sm sm:text-base text-gray-700">幅広い業務を通じて、専門知識とスキルを身につけられます。</p>
            </div>

            <div className="text-center p-4 sm:p-6 bg-blue-50 rounded-xl">
              <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                <i className="ri-heart-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2 sm:mb-3">やりがいのある仕事</h3>
              <p className="text-sm sm:text-base text-gray-700">お客様の課題解決をサポートし、感謝される仕事です。</p>
            </div>
          </div>
        </div>
      </section>

      {/* Positions Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 sm:mb-4">募集職種</h2>
            <p className="text-base sm:text-lg text-gray-600">現在募集中の職種をご紹介します</p>
          </div>

          <div className="space-y-8 sm:space-y-12">
            {positions.map((position, index) => (
              <div key={index} className="bg-white rounded-2xl overflow-hidden shadow-xl">
                <div className="grid lg:grid-cols-2 gap-0">
                  <div className="h-full">
                    <img
                      src={position.image}
                      alt={position.title}
                      className="w-full h-full object-cover object-top min-h-[300px] sm:min-h-[400px]"
                    />
                  </div>
                  <div className="p-6 sm:p-8 lg:p-10 xl:p-12">
                    <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                      <span className="px-3 sm:px-4 py-1 sm:py-1.5 bg-blue-100 text-blue-700 text-xs sm:text-sm font-semibold rounded-full whitespace-nowrap">
                        {position.type}
                      </span>
                      <span className="px-3 sm:px-4 py-1 sm:py-1.5 bg-green-100 text-green-700 text-xs sm:text-sm font-semibold rounded-full whitespace-nowrap">
                        募集中
                      </span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">{position.title}</h3>
                    <p className="text-lg sm:text-xl text-blue-600 font-semibold mb-3 sm:mb-4">{position.salary}</p>
                    <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4 sm:mb-6">{position.description}</p>
                    
                    <button
                      onClick={() => setSelectedPosition(selectedPosition === index ? null : index)}
                      className="flex items-center gap-2 text-blue-600 font-semibold hover:text-blue-700 transition-colors cursor-pointer whitespace-nowrap text-sm sm:text-base"
                    >
                      {selectedPosition === index ? '詳細を閉じる' : '詳細を見る'}
                      <i className={`ri-arrow-${selectedPosition === index ? 'up' : 'down'}-s-line text-lg sm:text-xl`}></i>
                    </button>
                  </div>
                </div>

                {selectedPosition === index && (
                  <div className="px-6 sm:px-8 lg:px-10 xl:px-12 pb-6 sm:pb-8 lg:pb-10 xl:pb-12 bg-gray-50">
                    <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
                      <div>
                        <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">仕事内容</h4>
                        <ul className="space-y-2 mb-6 sm:mb-8">
                          {position.responsibilities.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2 sm:gap-3">
                              <i className="ri-check-line text-blue-600 text-base sm:text-lg mt-0.5 flex-shrink-0"></i>
                              <span className="text-sm sm:text-base text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>

                        <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">応募資格</h4>
                        <ul className="space-y-2">
                          {position.requirements.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2 sm:gap-3">
                              <i className="ri-checkbox-circle-line text-blue-600 text-base sm:text-lg mt-0.5 flex-shrink-0"></i>
                              <span className="text-sm sm:text-base text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div>
                        <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">歓迎条件</h4>
                        <ul className="space-y-2 mb-6 sm:mb-8">
                          {position.preferred.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2 sm:gap-3">
                              <i className="ri-star-line text-yellow-500 text-base sm:text-lg mt-0.5 flex-shrink-0"></i>
                              <span className="text-sm sm:text-base text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>

                        <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">待遇・福利厚生</h4>
                        <ul className="space-y-2">
                          {position.benefits.map((item, idx) => (
                            <li key={idx} className="flex items-start gap-2 sm:gap-3">
                              <i className="ri-gift-line text-green-600 text-base sm:text-lg mt-0.5 flex-shrink-0"></i>
                              <span className="text-sm sm:text-base text-gray-700">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="mt-6 sm:mt-8 pt-6 sm:pt-8 border-t border-gray-200 grid md:grid-cols-3 gap-4 sm:gap-6">
                      <div>
                        <h5 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">勤務時間</h5>
                        <p className="text-sm sm:text-base text-gray-700">{position.workingHours}</p>
                      </div>
                      <div>
                        <h5 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">休日・休暇</h5>
                        <p className="text-sm sm:text-base text-gray-700">{position.holidays}</p>
                      </div>
                      <div>
                        <h5 className="font-bold text-gray-900 mb-2 text-sm sm:text-base">勤務地</h5>
                        <p className="text-sm sm:text-base text-gray-700">{position.location}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Section */}
      <section className="py-12 sm:py-16 lg:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-4 sm:mb-6">応募方法</h2>
            <p className="text-base sm:text-lg text-gray-700 leading-relaxed">
              ご応募をお待ちしております。まずはお気軽にお電話またはメールでお問い合わせください。
            </p>
          </div>

          <div className="bg-blue-50 rounded-2xl p-6 sm:p-8 lg:p-10">
            <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg sm:text-xl">1</span>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">お問い合わせ</h3>
                  <p className="text-sm sm:text-base text-gray-700">お電話またはメールでご連絡ください。応募に関するご質問もお気軽にどうぞ。</p>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg sm:text-xl">2</span>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">書類選考</h3>
                  <p className="text-sm sm:text-base text-gray-700">履歴書・職務経歴書をご提出ください（郵送またはメール）。</p>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg sm:text-xl">3</span>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">面接</h3>
                  <p className="text-sm sm:text-base text-gray-700">書類選考通過者の方に面接のご案内をいたします。</p>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-600 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-lg sm:text-xl">4</span>
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 sm:mb-2">内定・入社</h3>
                  <p className="text-sm sm:text-base text-gray-700">面接後、合否をご連絡いたします。入社日はご相談に応じます。</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <a
                href="tel:0860123456"
                className="flex-1 flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-blue-600 text-white text-base sm:text-lg font-semibold rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-fill text-xl sm:text-2xl"></i>
                086-012-3456
              </a>
              <a
                href="/#contact"
                className="flex-1 flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 bg-white text-blue-600 text-base sm:text-lg font-semibold rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer border-2 border-blue-600"
              >
                <i className="ri-mail-fill text-xl sm:text-2xl"></i>
                メールで問い合わせ
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}